import React, { useState, useEffect } from 'react'
import { styled } from '@mui/material/styles'
import { useDispatch, useSelector } from 'react-redux'
import { Amplify, Auth, API } from 'aws-amplify'
import { useFeatures } from 'flagged'
import awsconfig from '../../aws-exports'
import {
  Backdrop,
  Button,
  Box,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
} from '../../components/mui/Core/index'
import GenericNav from '../../components/genericcomponents/navbars/GenericNav'
import GenericSnackbar from '../../components/GenericSnackbar/generic-snackbar'
import ErrorState from '../../components/ErrorState/error-state'
import { getClientWarehouseCode } from '../../api/shipping/get-client-warehouse-code'
import { generatePassword } from '../../utils/generate-password'
import TableSearch from '../../components/TableSearch/table-search'
import useBrowserTabTitles from '../../hooks/browserTabTitles/browser-tab-titles'
import {
  fetchAllUsers,
  resetFetchAllUsersError,
  fetchAllRoles,
  resetFetchAllRolesError,
} from './user-management-slice'
import UserManagementTable from './components/UserManagementTable/user-management-table'
import Constants from '../../constants/en.json'
import { signupUser } from '../../api/user/signup-user'
import { userTypeConstants } from '../../constants/user-constants'
import { errorCodes } from '../../constants/error-codes'
import { setSnackbar } from '../../components/GenericSnackbar/generic-snackbar-slice'
import PageTitle from '../../components/PageTitle/page-title'
import InviteUserModal from './components/InviteUserModal/invite-user-modal'
import DeactivateUserModal from './components/DeactivateUserModal/deactivate-user-modal'
import {
  fetchClients,
  fetchCustomers,
  resetFetchClientsError,
  resetFetchCustomersError,
} from '../userauth/login/static-data-slice'
import SubHeader from '../../components/SubHeader/sub-header'
import { userPermissionConstants } from '../../constants/user-permission-constants'
import { hasUserPermission } from '../../utils/user-utils'
import { USER_MANAGEMENT_STATUS_OPTIONS } from '../../constants/status'
import UserRoleUpdateModal from './components/UserRoleUpdateModal/user-role-update-modal'
import {
  resetUpdateRoleConfirm,
  setCurrentStep,
  setSelectedRole,
} from './components/UserRoleUpdateModal/update-user-role-slice'
import { updateUserRole } from '../../api/user/update-user-role'
import { setAuthenticatedUser } from '../userauth/login/LogIn/log-in-slice'

Amplify.configure(awsconfig)

const CenteredContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  height: '341px',
  width: '480px',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: theme.palette.common.white,
  border: 'none !important',
  borderRadius: '12px',
  boxShadow: 24,
}))

function UserManagement() {
  useBrowserTabTitles(Constants.userManagement.PAGE_TITLE)
  const features = useFeatures()
  const dispatch = useDispatch()
  const isUserServiceEnabled = features.isUserServiceEnabled
  const [formData, setFormData] = useState({})
  const [emailAddress, setEmailAddress] = useState('')
  const clients = useSelector((state) => state.staticData.clients)
  const [rowSelection, setRowSelection] = useState([])
  const [isRowSelected, setIsRowSelected] = useState(null)
  const [isInviteModalOpen, setInviteModelOpen] = useState(false)
  const [showUserRoleUpdateModalOpen, setShowUserRoleUpdateModalOpen] = useState(false)
  const [selectedUserToUpdateRole, setSelectedUserToUpdateRole] = useState(null)
  const authenticatedUser = useSelector((state) => state.login.authenticatedUser)
  const searchByOptions = [
    { id: 'firstName', text: Constants.userManagement.FIRST_NAME, type: 'string' },
    { id: 'lastName', text: Constants.userManagement.LAST_NAME, type: 'string' },
    { id: 'clientName', text: Constants.userManagement.ORGANIZATION, type: 'string' },
    { id: 'email', text: Constants.userManagement.EMAIL, type: 'string' },
    { id: 'role', text: Constants.userManagement.ROLE, type: 'string' },
    { id: 'dateAdded', text: Constants.userManagement.DATE_ADDED, type: 'date' },
    { id: 'addedBy', text: Constants.userManagement.ADDED_BY, type: 'string' },
    { id: 'status', text: Constants.userManagement.STATUS, type: 'dropdown' },
  ]
  const [filterValue, setFilterValue] = useState('')
  const [filterBy, setFilterBy] = useState(searchByOptions[0].id)
  const isFetchingAllUsers = useSelector((state) => state.userManagement.isFetchingAllUsers)
  const fetchAllUsersError = useSelector((state) => state.userManagement.fetchAllUsersError)
  const isFetchingAllRoles = useSelector((state) => state.userManagement.isFetchingAllRoles)
  const fetchAllRolesError = useSelector((state) => state.userManagement.fetchAllRolesError)
  const selectedRoleForUpdate = useSelector((state) => state.updateUserRole.selectedRole)
  const roles = useSelector((state) => state.userManagement.roles)
  const oldRoles = [
    { roleName: userTypeConstants.CLIENT, roleDescription: userTypeConstants.CLIENT },
  ]
  const defaultRole = getDefaultRole()
  const isFetchingClients = useSelector((state) => state.staticData.isFetchingClients)
  const fetchClientsError = useSelector((state) => state.staticData.fetchClientsError)
  const isFetchingCustomers = useSelector((state) => state.staticData.isFetchingCustomers)
  const fetchCustomersError = useSelector((state) => state.staticData.fetchCustomersError)
  const [selectState, setSelectState] = useState({
    clientName: '',
    clientCode: '',
    warehouseCode: '',
    dms: '',
    clientRole: defaultRole?.roleName,
  })
  const isFormValid = !emailAddress || !selectState.clientName || !selectState.clientRole
  const isLoading =
    isFetchingAllUsers || isFetchingAllRoles || isFetchingClients || isFetchingCustomers
  const [isCreateUserModalOpen, setCreateUserModalOpen] = useState(false)
  const [isDeactivateUserModalOpen, setDeactivateUserModalOpen] = useState(false)
  const hasUserManagementAccess = hasUserPermission({
    permissionCode: userPermissionConstants.VIEW_USERS,
    permissions: authenticatedUser?.permissions || [],
  })
  const isAdmin = isUserServiceEnabled
    ? hasUserManagementAccess
    : authenticatedUser?.userType === 'LSLAdmin' || authenticatedUser?.userType === 'ClientAdmin'

  useEffect(() => {
    /* Displaying errors in snackbar */
    if (fetchAllUsersError) {
      dispatch(
        setSnackbar({ message: fetchAllUsersError?.message, severity: 'error', action: true }),
      )
    }
    if (fetchAllRolesError) {
      dispatch(
        setSnackbar({ message: fetchAllRolesError?.message, severity: 'error', action: true }),
      )
    }
    if (fetchClientsError) {
      dispatch(
        setSnackbar({ message: fetchClientsError?.message, severity: 'error', action: true }),
      )
    }
    if (fetchCustomersError) {
      dispatch(
        setSnackbar({ message: fetchCustomersError?.message, severity: 'error', action: true }),
      )
    }
  }, [fetchAllRolesError, fetchAllUsersError, dispatch, fetchClientsError, fetchCustomersError])

  const handleSnackbarClose = () => {
    dispatch(resetFetchAllUsersError())
    dispatch(resetFetchAllRolesError())
    dispatch(resetFetchClientsError())
    dispatch(resetFetchCustomersError())
  }

  useEffect(() => {
    /* Fetching list of organizations */
    /* TODO: Move these calls to on login success.
             Will be needed for client selection.
    */
    if (isAdmin) {
      dispatch(fetchClients())
      if (isUserServiceEnabled) {
        dispatch(fetchCustomers())
      }
    }
  }, [dispatch, isUserServiceEnabled, isAdmin])

  useEffect(() => {
    /* Fetch all users and roles on loading the view */
    if (isAdmin) {
      dispatch(fetchAllUsers({ isUserServiceEnabled }))
      dispatch(fetchAllRoles({ isUserServiceEnabled }))
    }
  }, [dispatch, isAdmin, isUserServiceEnabled])

  useEffect(() => {
    /* Setting form data when input changes */
    setFormData({
      email: emailAddress,
      username: emailAddress,
      clientName: selectState.clientName,
      clientCode: selectState.clientCode,
      warehouseCode: selectState.warehouseCode,
      dms: selectState.dms,
      userType: selectState.clientRole,
    })
  }, [
    emailAddress,
    selectState.clientName,
    selectState.clientCode,
    selectState.warehouseCode,
    selectState.dms,
    selectState.clientRole,
  ])

  function getDefaultRole() {
    const defaultClientPortalRole = roles?.filter((role) => {
      return role.roleDescription === userTypeConstants.CLIENT
    })
    // TODO: Get the default role for Customer Ordering users.
    return isUserServiceEnabled ? defaultClientPortalRole?.[0] : oldRoles?.[0]
  }

  const toggleInviteUserModal = () => {
    if (!isUserServiceEnabled) {
      setInviteModelOpen(true)
    }
    if (isUserServiceEnabled) {
      setCreateUserModalOpen(true)
    }
  }

  const resetInviteModal = () => {
    if (!isUserServiceEnabled) {
      setInviteModelOpen(false)
      setEmailAddress('')
      setSelectState({
        clientName: '',
        clientCode: '',
        warehouseCode: '',
        dms: '',
        clientRole: defaultRole?.roleName || userTypeConstants.CLIENT,
      })
    }
    if (isUserServiceEnabled) {
      setCreateUserModalOpen(false)
    }
  }

  const toggleDeactivateUserModal = async () => {
    setDeactivateUserModalOpen(true)
  }

  const resetDeactivateModal = () => {
    setDeactivateUserModalOpen(false)
  }

  const handleOnCreateUserSuccess = () => {
    resetInviteModal()
    dispatch(fetchAllUsers({ isUserServiceEnabled }))
    const message = Constants.userManagement.INVITE_SENT_SUCCESSFULLY.replace(
      '__USERNAME__',
      formData.username,
    )
    dispatch(setSnackbar({ message, severity: 'success', action: true }))
  }

  const handleOnCreateUserError = () => {
    const message = Constants.userManagement.errors.UNABLE_TO_INVITE_USER.replace(
      '__USERNAME__',
      formData.username,
    )
    dispatch(setSnackbar({ message, severity: 'error', action: true }))
  }

  const adminCreateUser = async () => {
    const temporaryPassword = generatePassword()
    if (!isUserServiceEnabled) {
      const apiName = 'AdminQueries'
      const path = '/createUser'
      const request = {
        body: {
          username: formData.username,
          password: temporaryPassword,
          email: formData.email,
          website: authenticatedUser?.website,
          clientName: formData.clientName,
          clientCode: formData.clientCode,
          whse_code: formData.warehouseCode,
          dms: formData.dms,
          clientRole: formData.userType,
          invitedBy: authenticatedUser?.email,
          dmsdb: authenticatedUser?.dmsdb,
          wmsdb: authenticatedUser?.wmsdb,
          profile: authenticatedUser?.profile,
        },
        headers: {
          'Content-Type': 'application/json',
          Authorization: `${(await Auth.currentSession()).getAccessToken().getJwtToken()}`,
        },
      }
      await API.post(apiName, path, request)
        .then(() => handleOnCreateUserSuccess())
        .catch(() => handleOnCreateUserError())
    }
    if (isUserServiceEnabled) {
      /* TODO:  Refactor request body when supporting
                Customer Ordering Portal. BE can derived
                some of the values off other attributes */
      const request = {
        userName: formData.email,
        email: formData.email,
        clientName: formData.clientName,
        clientCode: formData.clientCode,
        warehouseCode: formData.warehouseCode,
        dmsFlag: formData.dms,
        // TODO: These values will be derived based on the role
        customerNumber: '',
        customerName: '',
        invitedBy: authenticatedUser?.email,
        // TODO: remove as part of integrating customer portal user invite
        websiteURL: authenticatedUser?.website,
        // TODO:  Supporting multi-role selection
        roles: [selectState.clientRole],
        // TODO:  App Name will be derived from Role Selected
        appName: authenticatedUser.appName,
      }
      signupUser(request)
        .then(() => handleOnCreateUserSuccess())
        .catch(() => handleOnCreateUserError())
    }
  }

  const handleChangeOrganization = (event) => {
    const clientCode = event.target.getAttribute('code')
    const clientName = event.target.getAttribute('data-value')
    const dms = event.target.getAttribute('dms')
    if (clientCode) {
      getClientWarehouseCode(clientCode)
        .then((response) => {
          setSelectState({
            ...selectState,
            clientName,
            clientCode,
            dms,
            warehouseCode: response?.data.data.warehouseCodes,
          })
        })
        .catch(() => {
          const message = Constants.userManagement.errors.UNABLE_TO_FETCH_WAREHOUSE_DETAILS
          dispatch(setSnackbar({ message, severity: 'error', action: true }))
        })
    }
  }

  const handleSelectRole = (event) => {
    setSelectState({
      ...selectState,
      [event.target.name]: event.target.value,
    })
  }

  const handleRowSelection = (ids) => {
    setRowSelection(ids)
    setIsRowSelected(ids.length > 0)
  }

  const handleRoleUpdateModalOpen = (userData) => {
    setShowUserRoleUpdateModalOpen(true)
    setSelectedUserToUpdateRole({ ...userData })
  }

  const handleUpdateRoleModalClose = () => {
    setSelectedUserToUpdateRole(null)
    setShowUserRoleUpdateModalOpen(false)
    dispatch(setSelectedRole(null))
    dispatch(fetchAllUsers({ isUserServiceEnabled }))
    dispatch(setCurrentStep(1))
    dispatch(resetUpdateRoleConfirm())
  }

  const handleUpdateRole = async () => {
    if (isUserServiceEnabled) {
      const appName = authenticatedUser?.appName
      const rolesByAppName = roles?.filter((role) => role?.appName === appName)
      const roleNameFromRoleDescription = rolesByAppName?.find(
        (role) => role.roleDescription === selectedRoleForUpdate,
      )?.roleName
      const request = {
        userName: selectedUserToUpdateRole?.id,
        roleName: roleNameFromRoleDescription,
      }
      updateUserRole(request)
        .then((data) => onRoleUpdateSuccess(data))
        .catch((exception) => onRoleUpdateError(exception))
    }
  }

  function onRoleUpdateSuccess(data) {
    handleUpdateRoleModalClose()
    dispatch(fetchAllUsers({ isUserServiceEnabled }))
    const message = Constants.userManagement.updateRole.SUCCESS_UPDATE_ROLE.replace(
      '__USERNAME__',
      selectedUserToUpdateRole?.username,
    )
    dispatch(setSnackbar({ message, severity: 'success', action: true }))
    if (data?.data?.data) {
      const localAuthenticatedUser = { ...authenticatedUser }
      const newPermissions = data?.data?.data?.map((permission) => permission?.name)
      localAuthenticatedUser.permissions = newPermissions
      dispatch(setAuthenticatedUser(localAuthenticatedUser))
    }
  }

  function onRoleUpdateError(exception) {
    handleUpdateRoleModalClose()
    dispatch(setSnackbar({ message: exception?.message, severity: 'error', action: true }))
  }

  const returnSearch = (inputValue, selectedMenuItemIndex) => {
    setFilterValue(inputValue)
    setFilterBy(searchByOptions[selectedMenuItemIndex].id)
  }

  const tableSearchWrapperStyle = {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
  }

  const buttonStackStyle = {
    marginTop: 2,
    marginBottom: 4,
  }

  const modalActionsStyle = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    width: '100%',
    mt: '32px',
  }

  const userInviteModalStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'left',
    mx: '24px',
    mt: '16px',
  }

  const modalLabelStyle = {
    marginTop: '8px',
  }

  const modalInputStyle = {
    width: '203px',
    marginTop: '8px',
  }

  const modalSendInviteButtonStyle = {
    marginRight: 1,
    marginLeft: 2,
  }

  if (!isAdmin) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <GenericNav selected='User Management' user='authenticated' />
        <ErrorState errorCode={errorCodes.http.ERROR_UNAUTHORIZED} />
      </Box>
    )
  }

  if (isAdmin) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <GenericNav selected='User Management' user='authenticated' />
        <Backdrop open={isLoading}>
          <CircularProgress color='inherit' />
        </Backdrop>
        <Container maxWidth='xl'>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <PageTitle title={Constants.userManagement.PAGE_TITLE} />
            </Grid>
            <Grid size={{ xs: 6 }} display='flex' justifyContent='left' alignItems='flex-end'>
              <Stack spacing={4} direction='row' container='true' sx={buttonStackStyle}>
                <Button
                  variant='contained'
                  disabled={clients.length < 1}
                  onClick={toggleInviteUserModal}
                  color='secondary'
                >
                  {Constants.userManagement.inviteUser.INVITE_USER_BUTTON}
                </Button>
                <Button
                  onClick={toggleDeactivateUserModal}
                  color='error'
                  variant='contained'
                  disabled={!isRowSelected}
                >
                  {Constants.userManagement.deactivateUser.DEACTIVATE_USER}
                </Button>
              </Stack>
            </Grid>
            <Grid size={{ xs: 6 }} sx={tableSearchWrapperStyle}>
              <TableSearch
                returnSearch={returnSearch}
                searchByOptions={searchByOptions}
                statusOptions={USER_MANAGEMENT_STATUS_OPTIONS}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <UserManagementTable
                filterBy={filterBy}
                filterValue={filterValue}
                rowSelection={rowSelection}
                handleRowSelection={handleRowSelection}
                searchByOptions={searchByOptions}
                handleRoleUpdateModalOpen={handleRoleUpdateModalOpen}
              />
            </Grid>
          </Grid>
        </Container>
        <Modal
          open={isInviteModalOpen}
          onClose={() => {
            setInviteModelOpen(false)
          }}
        >
          <CenteredContainer>
            <Box sx={userInviteModalStyle}>
              <SubHeader title={Constants.userManagement.inviteUser.INVITE_USER} />
              <Typography
                mt={3}
                variant='label'
              >{`${Constants.userManagement.inviteUser.SEND_INVITE_TO}:`}</Typography>
              <TextField
                onChange={(e) => setEmailAddress(e.currentTarget.value)}
                required
                id='outlined-basic-email'
                label={Constants.userManagement.inviteUser.EMAIL}
                variant='outlined'
                type='email'
                placeholder={Constants.userManagement.inviteUser.EMAIL}
              />
              <Typography variant='label'>
                {Constants.userManagement.inviteUser.USER_CONFIGURATION}
              </Typography>
              <Grid container columnGap={6}>
                <FormControl>
                  <InputLabel sx={modalLabelStyle}>
                    {Constants.userManagement.inviteUser.CLIENT_ORGANIZATION}
                  </InputLabel>
                  <Select
                    name='clientName'
                    value={selectState.clientName}
                    onClick={handleChangeOrganization}
                    displayEmpty
                    sx={modalInputStyle}
                  >
                    {clients.map((item) => {
                      return (
                        <MenuItem
                          key={item.clientCode}
                          dms={item.dmsFlag}
                          code={item.clientCode}
                          value={item.clientName}
                        >
                          {item.clientName}
                        </MenuItem>
                      )
                    })}
                  </Select>
                </FormControl>
                <FormControl>
                  <InputLabel sx={modalLabelStyle}>
                    {Constants.userManagement.inviteUser.ROLE}
                  </InputLabel>
                  <Select
                    name='clientRole'
                    value={selectState.clientRole}
                    onChange={handleSelectRole}
                    sx={modalInputStyle}
                  >
                    {roles?.map((item) => {
                      return (
                        <MenuItem key={item.roleName} value={item.roleName}>
                          {item.roleDescription}
                        </MenuItem>
                      )
                    })}
                  </Select>
                </FormControl>
                <Grid size={{ xs: 12 }} sx={modalActionsStyle}>
                  <Button onClick={resetInviteModal} variant='outlined' color='secondary'>
                    {Constants.userManagement.inviteUser.CANCEL}
                  </Button>
                  <Button
                    onClick={adminCreateUser}
                    variant='contained'
                    color='secondary'
                    sx={modalSendInviteButtonStyle}
                    formdata={formData}
                    disabled={isFormValid}
                  >
                    {Constants.userManagement.inviteUser.SEND_INVITE}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </CenteredContainer>
        </Modal>
        <InviteUserModal isOpen={isCreateUserModalOpen} handleCloseModal={resetInviteModal} />
        <DeactivateUserModal
          isOpen={isDeactivateUserModalOpen}
          handleCloseModal={resetDeactivateModal}
          selectedUsers={rowSelection}
        />
        <UserRoleUpdateModal
          isOpen={showUserRoleUpdateModalOpen}
          selectedUser={selectedUserToUpdateRole}
          handleCloseAction={handleUpdateRoleModalClose}
          handleSubmitAction={handleUpdateRole}
        />
        <GenericSnackbar handleSnackbarClose={handleSnackbarClose} />
      </Box>
    )
  }
}

export default UserManagement
